import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StampCard } from "@/components/StampCard";
import { X, Loader2, Gift, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

export const LoyaltyModal = ({ open, onClose, restaurantId, program, accent = "#D9531E" }) => {
  const { t } = useT();
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const join = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post(`/restaurants/${restaurantId}/loyalty/join`, { phone, name });
      setCard(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setCard(null);
    setPhone("");
    setName("");
    setError("");
    onClose();
  };

  const copyLink = () => {
    const url = `${window.location.origin}/loyalty/${restaurantId}?card=${card.member.card_token}`;
    navigator.clipboard?.writeText(url);
    toast.success(t("cardLinkCopied"));
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end justify-center" onClick={close}>
      <div className="w-full max-w-md bg-[#FAF7F2] rounded-t-3xl p-5 pb-safe max-h-[90dvh] overflow-y-auto no-scrollbar fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-bold text-[#1F1D1B] flex items-center gap-2">
            <Gift className="h-6 w-6" style={{ color: accent }} /> {t("stampCard")}
          </h2>
          <button data-testid="close-loyalty-modal" onClick={close} className="text-[#6B6560]"><X className="h-6 w-6" /></button>
        </div>

        {!card ? (
          <form onSubmit={join} className="space-y-4">
            <p className="text-sm text-[#6B6560]">{t("enterPhoneStart", { reward: program?.reward_description, n: program?.stamps_required })}</p>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-[#6B6560]">{t("phone")}</label>
              <input data-testid="loyalty-phone-input" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" className="mt-1.5 w-full bg-white border border-[#E6E1DA] rounded-xl px-4 py-3 text-sm text-[#1F1D1B] focus:outline-none" style={{ caretColor: accent }} />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-[#6B6560]">{t("nameOptional")}</label>
              <input data-testid="loyalty-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("yourName")} className="mt-1.5 w-full bg-white border border-[#E6E1DA] rounded-xl px-4 py-3 text-sm text-[#1F1D1B] focus:outline-none" />
            </div>
            {error && <p data-testid="loyalty-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
            <button data-testid="loyalty-join-button" type="submit" disabled={loading} className="w-full rounded-full py-3.5 font-bold text-white flex items-center justify-center gap-2 disabled:opacity-60" style={{ backgroundColor: accent }}>
              {loading && <Loader2 className="h-5 w-5 animate-spin" />} {t("viewMyCard")}
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            {card.member.name && <p className="text-sm text-[#6B6560]">{t("welcomeName", { name: card.member.name })}</p>}
            <StampCard program={card.program} member={card.member} accent={accent} />
            <p className="text-xs text-center text-[#6B6560]">{t("askStaffStamp")}</p>
            <button data-testid="copy-card-link-button" onClick={copyLink} className="w-full inline-flex items-center justify-center gap-2 border border-[#E6E1DA] bg-white rounded-full py-3 text-sm font-medium text-[#1F1D1B]">
              <LinkIcon className="h-4 w-4" /> {t("saveCardLink")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
